import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Plan, Role } from '@ovrsee/shared'
import { createApiClient, type ApiClient, type TokenPair } from './api.js'

const STORAGE_KEY = 'ovrsee.tokens'

export function readTokens(): TokenPair | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const parsed = JSON.parse(raw) as Partial<TokenPair>
    if (typeof parsed.accessToken !== 'string' || typeof parsed.refreshToken !== 'string') return null
    return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken }
  } catch {
    return null
  }
}

export function writeTokens(tokens: TokenPair): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
}

export function clearStoredTokens(): void {
  localStorage.removeItem(STORAGE_KEY)
}

export interface CurrentUser {
  readonly id: string
  readonly name: string
  readonly email: string
  readonly role: Role
  readonly jobTitle: string | null
  readonly departmentId: string | null
  readonly organisation: {
    readonly id: string
    readonly name: string
    readonly plan: Plan
    readonly trialEndsAt: string | null
  }
}

interface AuthValue {
  readonly user: CurrentUser | null
  readonly isLoading: boolean
  readonly api: ApiClient
  signIn(tokens: TokenPair, user?: CurrentUser): Promise<void>
  signOut(): Promise<void>
  reload(): Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({
  children,
  baseUrl = '',
}: {
  children: ReactNode
  baseUrl?: string
}) {
  const [user, setUser] = useState<CurrentUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl,
        readTokens,
        writeTokens,
        onSignedOut: () => {
          clearStoredTokens()
          setUser(null)
        },
      }),
    [baseUrl],
  )

  const load = useCallback(async () => {
    if (readTokens() === null) {
      setUser(null)
      setIsLoading(false)
      return
    }
    try {
      setUser(await api.get<CurrentUser>('/api/me'))
    } catch {
      clearStoredTokens()
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  const signIn = useCallback(
    async (tokens: TokenPair, next?: CurrentUser) => {
      writeTokens(tokens)
      if (next !== undefined) {
        setUser(next)
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      await load()
    },
    [load],
  )

  const signOut = useCallback(async () => {
    const tokens = readTokens()
    if (tokens !== null) {
      // Best effort: a failed logout must still clear the client, or the user is stuck
      // signed in against an API that has already revoked them.
      await api.post('/api/auth/logout', { refreshToken: tokens.refreshToken }).catch(() => undefined)
    }
    clearStoredTokens()
    setUser(null)
  }, [api])

  const reload = load

  const value = useMemo<AuthValue>(
    () => ({ user, isLoading, api, signIn, signOut, reload }),
    [user, isLoading, api, signIn, signOut, reload],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (value === null) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
